import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { getProductReviews, getProductReview, getProductCategories } from "../lib/api";
import { SidebarBanner, BottomBanner } from "../components/Banner";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { 
  Star, StarHalf, ChevronRight, Filter, ThumbsUp, ThumbsDown, 
  Ship, Building, UtensilsCrossed, Tv, Refrigerator, Speaker, Backpack, Package,
  ArrowLeft, ExternalLink, Calendar
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const categoryIcons = {
  cruise: Ship,
  hotel: Building,
  restaurant: UtensilsCrossed,
  electronics: Tv,
  appliances: Refrigerator,
  audio: Speaker,
  travel_gear: Backpack,
  other: Package
};

function StarRating({ rating, size = "default" }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  const iconSize = size === "large" ? "h-6 w-6" : "h-4 w-4";
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} className={`${iconSize} fill-accent text-accent`} />
      ))}
      {hasHalf && <StarHalf className={`${iconSize} fill-accent text-accent`} />}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className={`${iconSize} text-muted-foreground/30`} />
      ))}
      <span className={`ml-2 font-semibold ${size === "large" ? "text-xl" : "text-sm"}`}>
        {rating.toFixed(1)}
      </span>
    </div>
  );
}

function ReviewCard({ review }) {
  const Icon = categoryIcons[review.category] || Package;
  const coverPhoto = review.photos?.[0];
  
  return (
    <Link 
      to={`/tests-produits/${review.slug || review.id}`}
      className="group bg-white border border-border rounded-lg overflow-hidden hover:border-accent/50 transition-all hover:shadow-md"
      data-testid={`review-card-${review.id}`}
    >
      {/* Image */}
      <div className="aspect-video bg-muted relative overflow-hidden">
        {coverPhoto ? (
          <img 
            src={`data:image/jpeg;base64,${coverPhoto.image_data}`}
            alt={review.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="h-16 w-16 text-muted-foreground/20" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm text-primary text-xs font-medium px-2 py-1 rounded">
            {review.category_label}
          </span>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-lg font-semibold text-primary line-clamp-2 group-hover:text-accent transition-colors">
            {review.title}
          </h3>
        </div>
        
        {review.brand && (
          <p className="text-sm text-muted-foreground mb-2">{review.brand} {review.model_name}</p>
        )}
        
        <StarRating rating={review.rating} />
        
        <p className="text-muted-foreground text-sm mt-3 line-clamp-2">{review.description}</p>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {format(new Date(review.created_at), "d MMM yyyy", { locale: fr })}
          </span>
          <span className="flex items-center text-accent text-sm font-medium group-hover:gap-2 transition-all">
            Lire le test
            <ChevronRight className="h-4 w-4 ml-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ProductReviewsListPage() {
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      const [reviewsRes, categoriesRes] = await Promise.all([
        getProductReviews(),
        getProductCategories() // Only returns visible categories
      ]);
      setReviews(reviewsRes.data);
      // Filter out hidden categories for public view
      setCategories(categoriesRes.data.filter(cat => cat.visible !== false));
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const params = selectedCategory !== "all" ? { category: selectedCategory } : {};
      const res = await getProductReviews(params);
      setReviews(res.data);
    } catch (error) {
      console.error("Error loading reviews:", error);
    }
  };

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="product-reviews-page">
      <SidebarBanner />
      
      {/* Header */}
      <section className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-16">
          <p className="font-accent text-accent tracking-wide mb-2">ITERCATUS</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary mb-4">
            Tests Produits
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Nos avis et tests sur les produits que nous utilisons : électronique, 
            électroménager, équipements de voyage, et bien plus.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-border sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
              className={selectedCategory === "all" ? "bg-primary" : ""}
            >
              Tous
            </Button>
            {categories.map(cat => {
              const Icon = categoryIcons[cat.value] || Package;
              return (
                <Button
                  key={cat.value}
                  variant={selectedCategory === cat.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`shrink-0 gap-2 ${selectedCategory === cat.value ? "bg-primary" : ""}`}
                >
                  <Icon className="h-4 w-4" />
                  {cat.label}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array(6).fill(0).map((_, i) => (
                <Skeleton key={i} className="aspect-[4/5]" />
              ))}
            </div>
          ) : reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map(review => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Package className="h-16 w-16 text-muted-foreground/20 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-primary mb-2">
                Aucun test disponible
              </h2>
              <p className="text-muted-foreground">
                {selectedCategory !== "all" 
                  ? "Aucun test dans cette catégorie pour le moment."
                  : "Les premiers tests arrivent bientôt !"}
              </p>
            </div>
          )}
        </div>
      </section>

      <BottomBanner />
    </div>
  );
}

export function ProductReviewDetailPage() {
  const { id } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadReview = async () => {
    try {
      const res = await getProductReview(id);
      setReview(res.data);
    } catch (error) {
      console.error("Error loading review:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary pt-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="aspect-video mb-8" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-primary mb-4">Test non trouvé</h1>
          <Link to="/tests-produits">
            <Button variant="outline">Retour aux tests</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = categoryIcons[review.category] || Package;

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="product-review-detail">
      <SidebarBanner />
      
      <article className="max-w-4xl mx-auto px-6 md:px-12 py-8 md:py-12 lg:max-w-5xl xl:max-w-6xl">
        {/* Back link */}
        <Link 
          to="/tests-produits" 
          className="inline-flex items-center text-muted-foreground hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux tests
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-accent/10 text-accent text-sm font-medium px-3 py-1 rounded-full">
              <Icon className="h-4 w-4" />
              {review.category_label}
            </span>
          </div>
          
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
            {review.title}
          </h1>
          
          {(review.brand || review.model_name) && (
            <p className="text-xl text-muted-foreground mb-4">
              {review.brand} {review.model_name}
            </p>
          )}
          
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <StarRating rating={review.rating} size="large" />
            {review.price && (
              <span className="text-lg font-semibold text-primary">{review.price}</span>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {format(new Date(review.created_at), "d MMMM yyyy", { locale: fr })}
            </span>
            {review.author_name && <span>Par {review.author_name}</span>}
          </div>
        </header>

        {/* Main Image */}
        {review.photos?.length > 0 && (
          <div className="mb-8">
            <img 
              src={`data:image/jpeg;base64,${review.photos[0].image_data}`}
              alt={review.title}
              className="w-full rounded-lg shadow-md"
            />
          </div>
        )}

        {/* Content */}
        <div className="bg-white border border-border rounded-lg p-6 md:p-8 mb-8">
          <p className="text-lg text-primary leading-relaxed whitespace-pre-wrap">
            {review.description}
          </p>
        </div>

        {/* Pros & Cons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {review.pros?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-green-700 mb-4">
                <ThumbsUp className="h-5 w-5" />
                Points forts
              </h3>
              <ul className="space-y-2">
                {review.pros.map((pro, i) => (
                  <li key={i} className="flex items-start gap-2 text-green-800">
                    <span className="text-green-500 mt-1">+</span>
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {review.cons?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-red-700 mb-4">
                <ThumbsDown className="h-5 w-5" />
                Points faibles
              </h3>
              <ul className="space-y-2">
                {review.cons.map((con, i) => (
                  <li key={i} className="flex items-start gap-2 text-red-800">
                    <span className="text-red-500 mt-1">-</span>
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Verdict */}
        {review.verdict && (
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-6 md:p-8 mb-8">
            <h3 className="font-heading text-xl font-semibold text-primary mb-4">Notre verdict</h3>
            <p className="text-primary leading-relaxed">{review.verdict}</p>
            <div className="mt-6 pt-6 border-t border-accent/20 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Note globale</p>
                <StarRating rating={review.rating} size="large" />
              </div>
              {review.purchase_link && (
                <a 
                  href={review.purchase_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3 rounded-lg hover:bg-accent/90 transition-colors"
                >
                  Voir le produit
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Additional Photos */}
        {review.photos?.length > 1 && (
          <div className="mb-8">
            <h3 className="font-heading text-xl font-semibold text-primary mb-4">Photos</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {review.photos.slice(1).map((photo, i) => (
                <img 
                  key={photo.id}
                  src={`data:image/jpeg;base64,${photo.image_data}`}
                  alt={`${review.title} - Photo ${i + 2}`}
                  className="w-full aspect-square object-cover rounded-lg"
                />
              ))}
            </div>
          </div>
        )}
      </article>

      <BottomBanner />
    </div>
  );
}
